const CATEGORIES_ID = 'categories'
const PRODUCTS_ID = 'producten'

const CATEGORY_ITEM_SELECTOR = '.category-item'
const PRODUCT_ITEM_SELECTOR = '.product-item'

const MAX_CLOSED_HEIGHT = 440

export default class TextContent {
  constructor(alignWithBottomRow = false) {
    this.nodes = null
    this.isOpen = false
    this.data = null
    this.timeoutId = null
    this.readMoreText = null
    this.alignWithBottomRow = alignWithBottomRow
    this.useImageAsAlignmentTarget = document.documentElement.classList.contains('theme22')

    this.init = this.init.bind(this)
    this.getNodes = this.getNodes.bind(this)
    this.updateData = this.updateData.bind(this)
    this.onWindowResize = this.onWindowResize.bind(this)
    this.toggleOpen = this.toggleOpen.bind(this)
    this.updateHeight = this.updateHeight.bind(this)

    this.nodes = this.getNodes()

    if (this.nodes) {
      const { readMore } = this.nodes
      this.readMoreText = { open: readMore.dataset.textOpen, close: readMore.dataset.textClose }

      this.init()
    }
  }

  get shouldHideReadMoreButton() {
    const { contentHeight, height, padding } = this.data
    return !this.isOpen && (contentHeight < (height - padding))
  }

  init() {
    const { innerContent, targets, readMore } = this.nodes
    const targetImages = this.useImageAsAlignmentTarget
      ? targets
      : targets.map((target) => target.querySelector('img')).filter((img) => !!img)

    const images = [
      ...Array.from(innerContent.querySelectorAll('img')),
      ...targetImages
    ]

    const loop = () => {
      if (!images.every((image) => image.complete)) {
        requestAnimationFrame(loop)
        return
      }

      this.updateData()
      this.updateHeight()

      readMore.addEventListener('click', this.toggleOpen)
      window.addEventListener('resize', this.onWindowResize)
    }

    requestAnimationFrame(loop)
  }

  onWindowResize() {
    clearTimeout(this.timeoutId)
    this.timeoutId = setTimeout(() => {
      this.updateData()
      this.updateHeight()
    }, 100)
  }

  hideReadMoreButton() {
    const { readMore } = this.nodes
    readMore.style.visibility = 'hidden'
    readMore.style.position = 'absolute'
  }

  showReadMoreButton() {
    const { readMore } = this.nodes
    readMore.style.removeProperty('visibility')
    readMore.style.removeProperty('position')
  }

  updateHeight() {
    const { inner, column } = this.nodes
    const {
      height,
      padding,
      readMoreHeight,
      contentHeight,
      targetRect,
    } = this.data

    column.style.marginBottom = targetRect ? '' : '30px'

    if (targetRect) {
      const calculatedHeight = this.shouldHideReadMoreButton
        ? (height - padding)
        : (height - padding - readMoreHeight)
      inner.style.height = `${calculatedHeight}px`
    } else {
      inner.style.height = `${contentHeight < height ? contentHeight : height}px`
    }

    if (this.shouldHideReadMoreButton) {
      this.hideReadMoreButton()
    } else {
      this.showReadMoreButton()
    }
  }

  toggleOpen() {
    this.isOpen = !this.isOpen

    const { inner, readMore, outer } = this.nodes

    readMore.textContent = this.isOpen ? this.readMoreText.close : this.readMoreText.open
    inner.classList.toggle('open')
    readMore.classList.toggle('open')
    outer.classList.toggle('opened')

    this.removeExistingClone();

    if (this.isOpen) {
        const clone = outer.cloneNode(true)
        clone.classList.add('text-block__clone')
        outer.parentElement.appendChild(clone);
        clone.querySelector('.text-block-read-more').addEventListener('click', () => {
            this.toggleOpen();
        })
    }

    this.updateHeight()
  }

  removeExistingClone() {
    const { outer } = this.nodes
    const existingTextBlockCloneElm = document.getElementsByClassName('text-block__clone')[0] || null;
    if (existingTextBlockCloneElm) {
      outer.parentElement.removeChild(existingTextBlockCloneElm)
    }
  }

  getTargetRect(data) {
    const {
      outerRect,
      contentHeight,
      itemRowRects,
      padding,
    } = data

    if (itemRowRects.length === 0) {
      return null
    }

    const contentRow = itemRowRects.findIndex(({ top }) => (
      Math.abs(top - outerRect.top) <= 5
    ))
    if (contentRow === -1) {
      return null
    }

    const targetRows = itemRowRects.slice(contentRow)
    if (this.alignWithBottomRow) {
      return targetRows[targetRows.length - 1]
    }
    
    if (
      (targetRows.length === 1) ||
      ((contentHeight + padding) <= targetRows[0].height && !this.alignWithBottomRow)
    ) {
      return targetRows[0]
    }
    
    return targetRows[1]
  }

  getNodes() {
    const column = document.getElementById('text-blocks')
    if (!column) {
      return null
    }
    
    const inner = document.querySelector('.text-block-inner')
    if (!inner) {
      return null
    }

    const innerContent = document.querySelector('.text-block-inner__content')
    if (!innerContent) {
      return null
    }

    const outer = inner.parentElement
    const readMore = document.querySelector('.text-block-read-more')
    if (!readMore) {
      return null
    }
    
    const categories = document.getElementById(CATEGORIES_ID)
    const products = document.getElementById(PRODUCTS_ID)
    if (!(categories || products)) {
      return null
    }

    let nodeList = categories
      ? categories.querySelectorAll(CATEGORY_ITEM_SELECTOR)
      : products.querySelectorAll(PRODUCT_ITEM_SELECTOR)
      
    if (nodeList.length === 0) {
      return null
    }

    nodeList = Array.from(nodeList).filter((elm) => {
      return !elm.classList.contains('product-item--charity')
    })

    return {
      column,
      inner,
      outer,
      innerContent,
      readMore,
      targets: this.useImageAsAlignmentTarget
        ? nodeList.map((node) => node.querySelector('img')).filter((img) => !!img)
        : nodeList
    }
  }

  updateData() {
    const { innerContent, outer, readMore, targets } = this.nodes

    const contentHeight = innerContent.clientHeight

    const itemRowRects = []
    targets.forEach((target) => {
      const rect = target.getBoundingClientRect()
      const lastRow = itemRowRects.length > 0 ? itemRowRects[itemRowRects.length - 1] : null
      if (!lastRow) {
        itemRowRects.push(rect)
      } else if (rect.top > lastRow.top) {
        itemRowRects.push(rect)
      }
    })

    const outerRect = outer.getBoundingClientRect()

    const outerStyles = window.getComputedStyle(outer)
    const readMoreStyles = window.getComputedStyle(readMore)

    const outerPaddingTop = parseInt(outerStyles.getPropertyValue('padding-top'))
    const outerPaddingBottom = parseInt(outerStyles.getPropertyValue('padding-bottom'))

    const readMoreMarginTop = parseInt(readMoreStyles.getPropertyValue('margin-top'))
    const readMoreHeight = (readMore.clientHeight + readMoreMarginTop)

    const data = {
      height: MAX_CLOSED_HEIGHT,
      minHeight: 0,
      readMoreHeight,
      contentHeight,
      outerRect,
      itemRowRects,
      padding: (outerPaddingTop + outerPaddingBottom),
    }

    const targetRect = this.getTargetRect(data)

    this.data = !targetRect
      ? data
      : {
        ...data,
        targetRect,
        height: (targetRect.bottom - outerRect.top),
        minHeight: targetRect.height * 2,
      }
  }
}
